/**
 * ArticleToc (G2, D9, R5): the screen outline's h2 headings (FlexibleLayout, Task 4) when there are at
 * least `minHeadings` (default 4). Desktop: an open nav list; phone: a collapsed <details>; the style
 * block toggles them at the phone breakpoint. A leading "1." is hung in the number column.
 */
import React, { memo } from 'react';
import { ChevronDown } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/articleIcons';
import type { ArticleBlockProps } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/types';
import { resolveLocaleText } from '@modules/screen-view-v2/utils/articleText';

const DEFAULT_MIN_HEADINGS = 4;
const SECTIONS_WORD = 'sections';

export const ArticleToc = memo(function ArticleToc({ section, locale, article }: ArticleBlockProps): React.ReactElement | null {
  const content = section.articleTocContent;
  const headings = (article.outline?.headings ?? []).filter((heading) => heading.tag === 'h2');
  const minimum = content?.minHeadings ?? DEFAULT_MIN_HEADINGS;
  if (headings.length === 0 || headings.length < minimum) return null;
  const label = resolveLocaleText(content?.label, locale);
  const rows = (
    <ol className="ap-toc-list">
      {headings.map((heading) => {
        const numbered = /^(\d+\.)\s*(.*)$/.exec(heading.text);
        return (
          <li key={heading.id}>
            <a className="ap-toc-link" href={`#${heading.id}`}>
              <span className="ap-toc-num" aria-hidden="true">{numbered ? numbered[1] : ''}</span>
              <span>{numbered ? numbered[2] : heading.text}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );
  return (
    <div className="ap-block ap-toc-wrap">
      <nav className="ap-toc ap-toc--desktop" aria-label={label || undefined}>
        {label ? <p className="ap-eyebrow">{label}</p> : null}
        {rows}
      </nav>
      <nav className="ap-toc ap-toc--phone" aria-label={label || undefined}>
        <details>
          <summary className="ap-toc-summary">
            <span className="ap-toc-summary-label">{label}</span>
            <span className="ap-toc-count">{`${headings.length} ${SECTIONS_WORD}`}</span>
            <ChevronDown className="ap-toc-chevron" />
          </summary>
          <div className="ap-toc-body">{rows}</div>
        </details>
      </nav>
    </div>
  );
});

ArticleToc.displayName = 'ArticleToc';
